/**
 * HCP Search Hook
 * Manages HCP search state, filters, and pagination
 * 
 * ARCHITECTURAL STANDARD: Reusable hook with API integration
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import useAPICall from '../../../../hooks/useAPICall';
import useDebounce from '../../../../hooks/useDebounce';
import usePagination from '../../../../hooks/usePagination';
import { API_CONFIG } from '../../../../config/api.config';
import { buildHcpQuery, normalizeSolrArray } from '../../../../utils/solrQuery';
import apiClient from '../../../../services/apiClient';

const defaultFilters = {
  basicText: '',
  advancedText: '',
  professions: '',
  specialities: '',
  gender: '',
  status: '',
  state: '',
  zip5: '',
  npiId: '',
  suffix: '',
  source: [],
  isGoldenUniverse: '',
};

const getInitialMode = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('type') === 'Advanced' ? 'advanced' : 'basic';
  } catch {
    return 'basic';
  }
};

/**
 * useHCPSearch Hook
 * @returns {Object} Search state and methods
 */
export const useHCPSearch = () => {
  const { loading, error, data, execute } = useAPICall();
  const [mode, setMode] = useState(getInitialMode());
  const [filters, setFilters] = useState(defaultFilters);
  const [facets, setFacets] = useState({
    professions: [],
    specialities: [],
    gender: [],
    status: [],
    state: [],
    suffix: [],
    source: [],
  });
  const [facetsLoading, setFacetsLoading] = useState(true);
  const [autoSearch, setAutoSearch] = useState(false);

  const {
    page,
    pageSize,
    setPage,
    setPageSize,
    reset: resetPagination,
  } = usePagination(1, 10);

  const debouncedFilters = useDebounce(filters, 500);
  const hasSearchedRef = useRef(false);
  const paginationRef = useRef({ page, pageSize });



  /**
   * Execute search
   */
  useEffect(() => {
    const loadFacets = async () => {
      setFacetsLoading(true);
      try {
        const response = await apiClient.post(API_CONFIG.hcp.searchFacet.endpoint, { q: 'q=*:* AND -is_deleted:true' });
        const fields = response?.data?.facet_counts?.facet_fields || {};
        const toValues = (arr) => (arr || []).filter((_, idx) => idx % 2 === 0);
        setFacets({
          professions: toValues(fields.professions),
          specialities: toValues(fields.specialities),
          gender: toValues(fields.gender),
          status: toValues(fields.status),
          state: toValues(fields.state),
          suffix: toValues(fields.sfx_cd),
          source: toValues(fields.source),
        });
      } catch {
        setFacets({
          professions: [],
          specialities: [],
          gender: [],
          status: [],
          state: [],
          suffix: [],
          source: [],
        });
      } finally {
        setFacetsLoading(false);
      }
    };
    loadFacets();
  }, []);

  const search = useCallback(async () => {
    try {
      const q = buildHcpQuery(filters, page, pageSize, mode);
      await execute(
        API_CONFIG.hcp.search,
        { q },
        {
          errorMessage: 'Unable to search HCPs. Please try again.',
          retries: 1,
        }
      );
    } catch (err) {
      // Error handled by useAPICall
      console.error('HCP search error:', err);
    } finally {
      hasSearchedRef.current = true;
    }
  }, [execute, filters, mode, page, pageSize]);

  const searchWithFilters = useCallback(async (nextFilters, nextPage = 1, nextPageSize = pageSize) => {
    try {
      const q = buildHcpQuery(nextFilters, nextPage, nextPageSize, mode);
      await execute(
        API_CONFIG.hcp.search,
        { q },
        {
          errorMessage: 'Unable to search HCPs. Please try again.',
          retries: 1,
        }
      );
    } catch (err) {
      console.error('HCP search error:', err);
    } finally {
      hasSearchedRef.current = true;
    }
  }, [execute, mode, pageSize]);

  /**
   * Auto-search when filters change (debounced)
   */
  useEffect(() => {
    const hasFilters = Object.values(debouncedFilters).some((value) => {
      if (Array.isArray(value)) return value.length > 0;
      return Boolean(value);
    });
    if (autoSearch && hasFilters) {
      resetPagination();
      search();
    }
  }, [debouncedFilters, autoSearch, search, resetPagination]);

  // Initial search (legacy behavior) - run once
  useEffect(() => {
    if (!hasSearchedRef.current) {
      search();
    }
  }, [search]);

  // Re-run search when pagination changes after initial search
  useEffect(() => {
    const previous = paginationRef.current;
    const changed = previous.page !== page || previous.pageSize !== pageSize;
    paginationRef.current = { page, pageSize };
    if (hasSearchedRef.current && changed) {
      search();
    }
  }, [page, pageSize, search]);

  /**
   * Update filters
   */
  const updateFilters = useCallback((newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const updateFiltersAndSearch = useCallback((newFilters) => {
    setFilters((prev) => {
      const nextFilters = { ...prev, ...newFilters };
      resetPagination();
      searchWithFilters(nextFilters, 1);
      return nextFilters;
    });
  }, [resetPagination, searchWithFilters]);

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setFilters({
      ...defaultFilters,
    });
    resetPagination();
    searchWithFilters(defaultFilters, 1);
  }, [resetPagination, searchWithFilters]);

  return {
    // State
    loading: loading || !hasSearchedRef.current,
    error,
    results: (data?.response?.docs || []).map((doc) => {
      const first = normalizeSolrArray(doc.fname);
      const middle = normalizeSolrArray(doc.mname);
      const last = normalizeSolrArray(doc.lname);
      const name = [first, middle, last].filter(Boolean).join(' ');
      const normalizedId = normalizeSolrArray(doc.mdm_id)
        || normalizeSolrArray(doc.ok_indv_id)
        || normalizeSolrArray(doc.combo_id)
        || normalizeSolrArray(doc.id);
      return {
        id: normalizedId,
        source: normalizeSolrArray(doc.source),
        mdmId: normalizeSolrArray(doc.mdm_id),
        firstName: first,
        middleName: middle,
        lastName: last,
        suffix: normalizeSolrArray(doc.sfx_cd),
        gender: normalizeSolrArray(doc.gender),
        isnId: normalizeSolrArray(doc.isn_id),
        status: normalizeSolrArray(doc.status),
        addressLine1: normalizeSolrArray(doc.addln1),
        city: normalizeSolrArray(doc.city),
        state: normalizeSolrArray(doc.state),
        zip: normalizeSolrArray(doc.zip5),
        businessName: normalizeSolrArray(doc.bus_name),
        professions: normalizeSolrArray(doc.professions),
        specialities: normalizeSolrArray(doc.specialities),
        degree: normalizeSolrArray(doc.degree),
        country: normalizeSolrArray(doc.cntry),
        okIndvId: normalizeSolrArray(doc.ok_indv_id),
        fullProfile: normalizeSolrArray(doc.is_golden_universe),
        amaPdrp: normalizeSolrArray(doc.ama_pdrp_ind),
        slnTypeName: normalizeSolrArray(doc.st_lic_typ_nm),
        createdDate: normalizeSolrArray(doc.cre_date),
        updatedDate: normalizeSolrArray(doc.vrfy_date),
        mdmCreatedTime: normalizeSolrArray(doc.mdm_created_time),
        mdmUpdatedTime: normalizeSolrArray(doc.mdm_updated_time),
        name: name || normalizeSolrArray(doc.bus_name) || 'Unknown',
        npi: normalizeSolrArray(doc.npi_id),
        specialty: normalizeSolrArray(doc.specialities),
        address: normalizeSolrArray(doc.addln1),
      };
    }),
    totalCount: data?.response?.numFound || 0,
    filters,
    facets,
    mode,
    page,
    pageSize,
    facetsLoading,
    autoSearch,
    
    // Methods
    search,
    updateFilters,
    updateFiltersAndSearch,
    clearFilters,
    setMode,
    setPage,
    setPageSize,
    setAutoSearch,
  };
};

export default useHCPSearch;
